import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as signupModal from "signupModal";
import stores from "stores/stores";
import FormattingOptions, {
  type FormattingOptionsProps,
} from "views/FormattingOptions";
import GuideView from "views/GuideView";
import { LanguageSelect } from "views/LanguageSelectView";
import { RemoteWordListToolbar } from "views/RemoteWordListToolbarView";
import { RemoteWordList } from "views/RemoteWordListView";
import SmartDictionaryView from "views/SmartDictionaryView";
import TranslationDirectionArrowView from "views/TranslationDirectionArrowView";

export type DictionaryPaneState = {
  fallbackText?: string;
  html?: string;
  /** The iOS app shows the dictionary's website natively, over the reader */
  native?: boolean;
  url?: string;
};

export type SmartDictionaryPaneState =
  | { mode: "empty" }
  | { mode: "local" }
  | { mode: "word"; props: any };

type PaneProps = {
  selected: boolean;
};

const selectedClass = (selected: boolean) => (selected ? "selected" : "");

export const SmartDictionaryPane = ({
  selected,
  state,
}: PaneProps & {
  state?: SmartDictionaryPaneState;
}) => (
  <div
    class={`tabContent ${selectedClass(selected)}`}
    data-tab="smartDictionary"
  >
    <div id="smartDictionaryContainer">
      {state?.mode === "local" ? (
        <p class="smartDictionaryContent trialMode">
          <a
            href="#"
            class="signup"
            onClick={(event) => {
              event.preventDefault();
              signupModal.show({
                heading: "Sign up for context explanations",
                stores,
              });
            }}
          >
            Sign up for free
          </a>{" "}
          for AI-powered context-aware explanations of words and phrases.
        </p>
      ) : state?.mode === "word" ? (
        <SmartDictionaryView {...state.props} />
      ) : (
        <p class="smartDictionaryContent">
          {deviceDetect.is("mobile") ? "Tap" : "Click"} any word for a
          context-aware explanation.
        </p>
      )}
    </div>
  </div>
);

class DictionaryHtml extends preact.Component<{
  html?: string;
}> {
  private containerRef = preact.createRef<HTMLDivElement>();
  // The sidebar's height changes as a bottom sheet on phones is expanded
  private sideBarResizeObserver?: ResizeObserver;

  componentDidMount() {
    this.updateDictionarySize();
    const sideBar = this.containerRef.current?.closest(".sideBar");
    if (sideBar && typeof ResizeObserver !== "undefined") {
      this.sideBarResizeObserver = new ResizeObserver(() =>
        this.updateDictionarySize()
      );
      this.sideBarResizeObserver.observe(sideBar);
    }
  }

  componentWillUnmount() {
    this.sideBarResizeObserver?.disconnect();
  }

  componentDidUpdate() {
    this.updateDictionarySize();
  }

  render() {
    return this.props.html ? (
      // Dictionary HTML is built upstream from third-party sources (e.g. an
      // embedded WordReference iframe). It must be injected verbatim.
      <div
        ref={this.containerRef}
        dangerouslySetInnerHTML={{ __html: this.props.html }}
      />
    ) : (
      <p ref={this.containerRef}>Click any word to translate it.</p>
    );
  }

  private updateDictionarySize = () => {
    window.setTimeout(() => {
      const container = this.containerRef.current?.parentElement;
      if (!container) return;

      const iframe = container.querySelector<HTMLIFrameElement>(
        "#wordReferenceIFrame"
      );
      if (!iframe) return;

      let scale = 1;
      if (/matrix/.test(getComputedStyle(iframe).transform)) {
        scale = 0.7;
      }

      const sideBar = container.closest(".sideBar");
      const titleBar = sideBar?.querySelector<HTMLElement>(".titleBar");
      const dictionaryChoice =
        sideBar?.querySelector<HTMLElement>("#dictionaryChoice");
      const height =
        ((sideBar?.clientHeight ?? window.innerHeight) -
          (titleBar?.offsetHeight ?? 0) -
          (dictionaryChoice?.offsetHeight ?? 0)) /
          scale -
        4;

      container.style.height = `${height}px`;
      iframe.style.height = `${height}px`;
      iframe.style.top = `${-iframe.offsetHeight * 0.5 * (1 - scale)}px`;
    }, 0);
  };
}

export const DictionaryPane = ({
  onOpenDictionary,
  onSourceLanguageChange,
  owner,
  selected,
  state,
}: PaneProps & {
  onOpenDictionary?: () => void;
  onSourceLanguageChange?: (languageCode: string) => void;
  owner?: boolean;
  state?: DictionaryPaneState;
}) => (
  <div class={`tabContent ${selectedClass(selected)}`} data-tab="dictionary">
    <div id="dictionaryChoice">
      <span id="fromLanguage" class="languageSelectView">
        <LanguageSelect
          learningLanguages
          field="currentLearningLanguage"
          hint={
            owner
              ? "Set the language of this document"
              : "Only the owner of the article can set its language"
          }
          onChange={onSourceLanguageChange}
          flipIfSame
          disabled={!owner}
          english
          confirmationPrompt={
            stores.bookModel?.book?.value?.public
              ? "This will change the language of the text for everyone. Are you sure?"
              : undefined
          }
        />
      </span>
      <TranslationDirectionArrowView />
      <span id="toLanguage" class="languageSelectView">
        <LanguageSelect
          field="firstLanguage"
          hint="Set your first language"
          switchToMonolingualIfSame
          english
        />
      </span>
      {state?.url && (
        <button id="openDictionary" class="noMobile" onClick={onOpenDictionary}>
          Open in new window
        </button>
      )}
    </div>
    {state?.fallbackText && <div id="fallback">{state.fallbackText}</div>}
    {/* The iOS app shows the dictionary in a sheet below, as this tab */}
    {!(state?.native && state.url) && (
      <div id="dictionaryContainer">
        <DictionaryHtml html={state?.html} />
      </div>
    )}
  </div>
);

export const GuidePane = ({ selected }: PaneProps) => (
  <div class={`tabContent ${selectedClass(selected)}`} data-tab="guide">
    {selected && <GuideView />}
  </div>
);

export const FormattingOptionsPane = ({
  formattingOptionsProps,
  selected,
}: PaneProps & {
  formattingOptionsProps?: FormattingOptionsProps;
}) => (
  <div
    class={`tabContent ${selectedClass(selected)}`}
    data-tab="formattingOptions"
  >
    {formattingOptionsProps && (
      <FormattingOptions {...formattingOptionsProps} />
    )}
  </div>
);

export const WordsPane = ({
  appRouter,
  localMode,
  selected,
}: PaneProps & {
  appRouter?: any;
  localMode?: boolean;
}) => (
  <div class={`tabContent ${selectedClass(selected)}`} data-tab="editWord">
    {localMode ? (
      <p style={{ marginLeft: "10px", marginRight: "10px" }}>
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            signupModal.show({
              stores,
            });
          }}
        >
          Sign up for free
        </a>{" "}
        to view and manage your words and phrases.
      </p>
    ) : appRouter ? (
      <>
        <div class="wordsToolbar">
          <RemoteWordListToolbar
            model={stores.remoteUserWords}
            stores={stores}
            appRouter={appRouter}
          />
        </div>
        <RemoteWordList
          model={stores.remoteUserWords}
          stores={stores}
          wordGroup=""
          showCancelButton={false}
          noWordsMessage="Click any word on the left to translate."
        />
      </>
    ) : (
      <>
        <p>Click any word on the left to translate.</p>
        <p>All translated words and phrases will be listed here.</p>
      </>
    )}
  </div>
);
